import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { IUpdateTicket } from '@/interface/betSlip';
import {
    BetSlipState, BetSlipType, DisplayMode, LeagueSize, MarketPage, OddsOption, OpenTicketStatus, PlaceOrderStatus, PlayerType, StatementDetailStatus, SwitchState,
} from '@/interface/enum';
import type { IVoucherDisplay } from '@/interface/voucher';
import Api, { fireAndForget } from '@/libraries/api';
import { saveLastStake } from '@/libraries/betSlip/lastStakeStorage';
import { calculateVoucherMaxPayout, getMaxPayout } from '@/libraries/betSlip/payout';
import { calculateStake, isStakeAtLeastMinBet, isStakeLessBalance, isStakeWithinMaxBet } from '@/libraries/betSlip/stake';
import { useSingleBetCashOut } from '@/libraries/composables/useBetSlipCashOut';
import useState from '@/libraries/composables/useState';
import { useSwitchStateTracker } from '@/libraries/composables/useSwitchStateTracker';
import { useVoucher } from '@/libraries/composables/useVoucher';
import type { IEuroTicket } from '@/libraries/euroTicket';
import { getOpenTicketErrorMessage } from '@/libraries/euroTicket';
import { getDefaultStake, isOddsClose, isPlaceHierarchyClosed } from '@/libraries/helpers/betSlipHelper';
import { sendEventToGA } from '@/libraries/helpers/gtmHelper';
import { platform } from '@/libraries/helpers/platformHelper';
import { isCorrectScoreMarket } from '@/libraries/oddsDisplay';
import { getSportsBsiUrl } from '@/libraries/url';
import { formatMinDecimalPlace } from '@/libraries/utils/text';
import { sleep } from '@/libraries/utils/utils';
import { useAccountStore } from '@/stores/accountStore';
import { useBetCountStore } from '@/stores/betCountStore';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useHeaderDisplayStore } from '@/stores/headerDisplayStore';
import { useRootStore } from '@/stores/rootStore';
import { EnumTournamentSizeType } from '@/types';

export const useSingleBetSlipStore = defineStore('singleBetSlip', () => {
    const [betSlip, setBetSlip] = useState<IEuroTicket>(getDefaultBetSlip());
    const [isPlacingBet, setIsPlacingBet] = useState(false);
    const [stake, setStake] = useState(0);

    const { setTopScreenToast, setShowAcceptAnyOddsBanner, setFailedPlaceBetShowAaoBanner } = useRootStore();
    const { brandName } = storeToRefs(useRootStore());
    const { customerInfo, balance } = storeToRefs(useAccountStore());
    const { formatMoneyWithSymbolAndDecimal, setBalance } = useAccountStore();
    const { placeBetDelayTime } = storeToRefs(useFeatureToggleStore());
    const { switchState, updateGaSwitchState, resetSwitchState } = useSwitchStateTracker();

    const voucherInput = computed(() => ({
        sportType: betSlip.value.sportType,
        leagueId: betSlip.value.leagueId,
        minBet: betSlip.value.minBet,
        maxBet: betSlip.value.maxBet,
        marketType: betSlip.value.marketType,
        price: betSlip.value.price,
        priceStyle: betSlip.value.priceStyle,
    }));

    const [isRedeemVoucher, setIsRedeemVoucher] = useState(false);
    const [voucherList, setVoucherList] = useState<IVoucherDisplay[]>([]);
    const { vouchers, fetchVouchers } = useVoucher(voucherInput);

    async function openTicket(isFirstTime: boolean) {
        if (betSlip.value.key === '') return;

        const request = {
            oddsId: betSlip.value.oddsId,
            option: betSlip.value.option,
            marketType: betSlip.value.marketType,
            sportType: betSlip.value.sportType,
            eventId: betSlip.value.eventId,
            isLive: betSlip.value.isLive,
        };

        const [ticketResponse] = await Promise.all([
            Api.openSingleTicket(request),
            fetchVouchers(),
        ]);

        const ticketKey = Object.keys(ticketResponse.oddsInfo)[0];
        const oddsData = ticketResponse.oddsInfo[ticketKey];
        const minDecimalPlace = isCorrectScoreMarket(betSlip.value.marketType) ? 0 : 2;

        const isTicketRemove = isOddsClose(oddsData.errorCode);
        if (isTicketRemove.shouldRemove) {
            setTopScreenToast({
                status: 'error',
                messageKey: isTicketRemove.message,
            });
            setTimeout(async () => {
                await removeTicket();
            }, 3000);
            return;
        }

        if (isFirstTime) {
            const { minBet, maxBet } = ticketResponse;
            setStake(getDefaultStake(
                minBet,
                maxBet,
                stake.value,
            ));
        }

        const ticketLiveHomeScore = betSlip.value.odds.eventResult.liveHomeScore;
        const ticketLiveAwayScore = betSlip.value.odds.eventResult.liveAwayScore;

        setBalance(ticketResponse.balance);
        updateTicket({
            key: ticketKey,
            oddsData: oddsData.errorCode === OpenTicketStatus.Success ? oddsData : null,
            maxBet: ticketResponse.maxBet,
            minBet: ticketResponse.minBet,
            messageKey: getOpenTicketErrorMessage(
                oddsData.errorCode,
                betSlip.value,
                oddsData,
                betSlip.value.priceStyle === oddsData.priceStyle,
            ),
            messageData: {
                oldOdds: formatMinDecimalPlace(betSlip.value.price, minDecimalPlace),
                newOdds: formatMinDecimalPlace(oddsData.price, minDecimalPlace),
                oldScore: `[${ticketLiveHomeScore}:${ticketLiveAwayScore}]`,
                newScore: `[${oddsData.liveHomeScore}:${oddsData.liveAwayScore}]`,
                oldPoint: betSlip.value.point.toFixed(2),
                newPoint: oddsData.point.toFixed(2),
            },
        });
        setVoucherList(vouchers.value);
    }

    const selectedVoucher = ref<IVoucherDisplay | null>(null);
    const stakeOrVoucherStake = computed(() => (isRedeemVoucher.value
        ? selectedVoucher.value?.stake ?? 0
        : stake.value));

    const maxPayout = computed(() => {
        const payout = isRedeemVoucher.value
            ? calculateVoucherMaxPayout(
                stakeOrVoucherStake.value,
                betSlip.value.price,
                betSlip.value.priceStyle,
            )
            : getMaxPayout(stakeOrVoucherStake.value, betSlip.value.price, betSlip.value.priceStyle);

        return formatMoneyWithSymbolAndDecimal(payout);
    });

    const isValidStake = computed(() => {
        if (isStakeWithinMaxBet(stakeOrVoucherStake.value, betSlip.value.maxBet)
            && isStakeAtLeastMinBet(stakeOrVoucherStake.value, betSlip.value.minBet)) {
            if (isRedeemVoucher.value) {
                return selectedVoucher.value?.isAvailable;
            }
            return isStakeLessBalance(
                stakeOrVoucherStake.value,
                balance.value,
                betSlip.value.price,
            );
        }
        return false;
    });

    const isValidBetSetting = computed(() => betSlip.value.minBet > 0 && betSlip.value.maxBet > 0);

    const betSlipCashOut = computed(() => ({
        sportType: betSlip.value.sportType,
        isMainMarket: betSlip.value.odds.marketGroup.id === 0,
        isLive: betSlip.value.isLive,
        leagueId: betSlip.value.leagueId,
        marketType: betSlip.value.marketType,
    }));

    const hasTicket = computed(() => betSlip.value.oddsId !== 0);

    const { isDisplayCashOutTooltip } = useSingleBetCashOut(betSlipCashOut);

    const { refreshBetCount } = useBetCountStore();
    const { setDisplayMode } = useHeaderDisplayStore();

    async function handleSuccessfulPlaceBet(orderStatus: StatementDetailStatus) {
        setTopScreenToast({
            status: 'placeBetSuccess',
            messageKey: orderStatus === StatementDetailStatus.Running
                ? 'betSlip_placeBetConfirmed'
                : 'betSlip_placeBetBeingProcessed',
        });

        if (isDisplayCashOutTooltip.value) {
            window.eventEmitter.emit('displayCashOutTooltip');
        }

        setTimeout(() => {
            refreshBetCount(false);
            platform.updateBalance();
            window.eventEmitter.emit('refreshBetList');
        }, 1000);

        if (!isRedeemVoucher.value) {
            saveLastStake(stake.value);
        }
        await removeTicket();

        setShowAcceptAnyOddsBanner(true);

        setTimeout(() => {
            setDisplayMode(DisplayMode.ShowAll);
        }, 600);
    }

    async function handleFailedPlaceBet(placeOrderStatus: PlaceOrderStatus, message: string, extraInfo: string) {
        if (
            placeOrderStatus === PlaceOrderStatus.PriceChange
            || placeOrderStatus === PlaceOrderStatus.HdpPointChange
            || placeOrderStatus === PlaceOrderStatus.ScoreChanged
        ) {
            await openTicket(false);
            return;
        }

        if (isPlaceHierarchyClosed(placeOrderStatus)) {
            window.location.href = `${getSportsBsiUrl()}`;
            return;
        }

        if (placeOrderStatus === PlaceOrderStatus.RetryOpenTicket) {
            await openTicket(false);
            setTopScreenToast({
                status: 'error',
                messageKey: 'betSlip_errorMessage_generalFailure',
            });
            return;
        }

        let messageKey = message;
        let messageData: { [key: string]: any } = {};
        const { playerType } = customerInfo.value;

        switch (placeOrderStatus) {
            case PlaceOrderStatus.MaxWinLoseLimitExceed:
                messageKey = playerType === PlayerType.B2C
                    ? 'betSlip_errorMessage_maxWinLossLimit_ContactCS'
                    : 'betSlip_errorMessage_maxWinLossLimit_ContactUpperline';
                break;
            case PlaceOrderStatus.LimitExceed: {
                const adjustedStake = parseInt(extraInfo);
                if (Number.isNaN(adjustedStake)) {
                    messageKey = 'betSlip_errorMessage_limitExceedTryLater';
                } else {
                    messageData = { alternateStake: adjustedStake };
                    setStake(adjustedStake);
                    if (Number.isNaN(adjustedStake)) {
                        throw new Error('placeBet:adjustedStake is NaN');
                    }
                }
                break;
            }
            case PlaceOrderStatus.OrderProcessing:
                messageKey = 'betSlip_errorMessage_orderProcessing';
                break;
            case PlaceOrderStatus.HierarachySuspend:
            case PlaceOrderStatus.AccountProblem:
            case PlaceOrderStatus.MemberSuspend:
                messageData = { brandName: brandName.value };
                break;
            default:
                break;
        }

        updateTicket({
            key: `${betSlip.value.oddsId}-${betSlip.value.option}`,
            messageKey,
            messageData,
        });
    }

    async function placeBet() {
        if (betSlip.value.key === '' || isPlacingBet.value) {
            return;
        }
        if (!isValidStake.value) {
            adjustStake();
            return;
        }
        setIsPlacingBet(true);

        const api = isRedeemVoucher.value ? Api.placeSingleVoucherBet : Api.placeSingleBet;
        if (isRedeemVoucher.value && selectedVoucher.value === null) {
            setIsPlacingBet(false);
            return;
        }

        const request = {
            eventId: betSlip.value.eventId,
            oddsId: betSlip.value.oddsId,
            option: betSlip.value.option,
            stake: stakeOrVoucherStake.value,
            uid: betSlip.value.uid,
            betPage: betSlip.value.betPage,
            sportType: betSlip.value.sportType,
            marketType: betSlip.value.marketType,
            voucherIdString: isRedeemVoucher.value ? selectedVoucher.value?.id : '',
            point: betSlip.value.point,
            liveScore: {
                home: betSlip.value.odds.eventResult.liveHomeScore,
                away: betSlip.value.odds.eventResult.liveAwayScore,
            },
        };

        try {
            const [{
                isPlaceBetSuccess,
                placeOrderStatus,
                orderStatus,
                extraInfo,
                message,
            }] = await Promise.all([api(request), sleep(placeBetDelayTime.value)]);

            if (isPlaceBetSuccess) {
                await handleSuccessfulPlaceBet(orderStatus);
            } else {
                await handleFailedPlaceBet(placeOrderStatus, message, extraInfo);
                setFailedPlaceBetShowAaoBanner(true);
            }
        } catch {
            setFailedPlaceBetShowAaoBanner(true);
        } finally {
            sendEventToGA('gtm.click', `SingleSlip_Btn_PlaceBet_${SwitchState[switchState.value]}`);
            setIsPlacingBet(false);
            resetSwitchState();
        }
    }

    function adjustStake() {
        const stakeInfo = calculateStake(
            betSlip.value.maxBet,
            betSlip.value.minBet,
            balance.value,
            stake.value,
            betSlip.value.price,
        );
        setStake(stakeInfo.stake);

        updateTicket({
            key: `${betSlip.value.oddsId}-${betSlip.value.option}`,
            messageKey: stakeInfo.message,
        });
    }

    const { setBetSlipState, setBetSlipType } = useBetSlipStore();

    function addTicket(ticket: IEuroTicket) {
        updateGaSwitchState(betSlip.value, ticket);
        setIsRedeemVoucher(false);
        setBetSlip(ticket);
        setBetSlipState(BetSlipState.Single);
        setBetSlipType(BetSlipType.Single);
    }

    async function removeTicket() {
        if (betSlip.value.key !== '') {
            fireAndForget(Api.clearTicket());
        }
        setBetSlip(getDefaultBetSlip());
        setBetSlipState(BetSlipState.Closed);
        setBetSlipType(BetSlipType.None);
        setIsRedeemVoucher(false);
    }

    async function clearTicketForFlowTransition() {
        const hasCurrentTicket = hasTicket.value;
        setBetSlip(getDefaultBetSlip());
        if (!hasCurrentTicket) {
            return;
        }
        try {
            await Api.clearTicket();
        } catch (error) {
            console.error(error);
        }
    }

    function updateTicket({
        key, maxBet, minBet, oddsData, messageKey, messageData,
    }: IUpdateTicket) {
        if (betSlip.value.key !== key) {
            return;
        }

        if (maxBet !== undefined) {
            betSlip.value.maxBet = maxBet;
        }

        if (minBet !== undefined) {
            betSlip.value.minBet = minBet;
        }

        if (oddsData) {
            if (betSlip.value.odds) {
                betSlip.value.odds.eventResult.liveHomeScore = oddsData.liveHomeScore;
                betSlip.value.odds.eventResult.liveAwayScore = oddsData.liveAwayScore;
            }
            betSlip.value.point = oddsData.point;
            betSlip.value.price = oddsData.price;
            betSlip.value.betCondition = oddsData.betCondition;
            betSlip.value.priceStyle = oddsData.priceStyle;
            betSlip.value.uid = oddsData.uid;
        }

        if (messageKey) {
            window.clearTimeout(betSlip.value.message.timeoutId);
            betSlip.value.message.key = messageKey;
            betSlip.value.message.data = messageData;
            betSlip.value.message.timeoutId = window.setTimeout(() => {
                betSlip.value.message.key = null;
                betSlip.value.message.data = null;
                betSlip.value.message.timeoutId = 0;
            }, 3000);
        }
    }

    function isTicketExist(key: string) {
        return betSlip.value.key === key;
    }

    return {
        betSlip,
        isPlacingBet,
        stake,
        selectedVoucher,
        maxPayout,
        isValidStake,
        isValidBetSetting,
        isRedeemVoucher,
        voucherList,
        hasTicket,
        setIsRedeemVoucher,
        addTicket,
        removeTicket,
        clearTicketForFlowTransition,
        updateTicket,
        setStake,
        setIsPlacingBet,
        openTicket,
        placeBet,
        setBetSlip,
        isTicketExist,
    };
});

function getDefaultBetSlip(): IEuroTicket {
    return {
        type: 'single',
        key: '',
        event: {
            type: 'match',
            eventDate: '',
            eventId: 0,
            isMixParlay: false,
            leagueId: 0,
            leagueName: [],
            leagueInformation: [],
            leagueSize: LeagueSize.Small,
            marketPage: MarketPage.Today,
            mainMarketEventResult: {
                extraInfo: {
                    awayRedCards: 0,
                    homeRedCards: 0,
                    injuryTime: 0,
                    period: 0,
                    periodStartTime: '',
                    round: 0,
                    statisticsUrl: '',
                },
                isMixParlay: false,
                liveAwayScore: 0,
                liveHomeScore: 0,
            },
            isTvLive: false,
            isSboLive: false,
            isHomeFavorite: false,
            isAwayFavorite: false,
            sportType: 0,
            eventResults: [],
            statisticsUrl: '',
            isToday: false,
            isClosed: false,
            homeTeam: { teamName: [] },
            awayTeam: { teamName: [] },
            tournament: {
                id: 0,
                isOlympicSpecialTournament: false,
                isSpecialTournament: false,
                isTestOnly: false,
                tournamentCode: '',
                tournamentSize: EnumTournamentSizeType.Small,
                eventsCount: 0,
            },
            eventCode: '',
            hasStatistics: false,
            liveCourtMatchId: 0,
            marketTypeCount: [],
            showTime: '',
            showTimeType: 0,
            isLive: false,
            isSimulation: false,
            matchOrder: 0,
            realKickoffTime: new Date(),
        },
        odds: {
            eventResult: {
                eventId: 0,
                liveAwayScore: 0,
                liveHomeScore: 0,
            },
            marketType: 0,
            marketDisplayTypeName: '',
            marketGroup: {
                id: 0,
                displayPriority: 0,
                homeTemplate: '',
                awayTemplate: '',
            },
            oddsId: 0,
            status: 0,
            eventId: 0,
            priceOption: [],
            betCondition: '',
            isLive: false,
            point: 0,
            sortOrder: 0,
            hasMixParlay: false,
        },
        priceOption: {
            option: '',
            oddsOption: OddsOption.Unknown,
            price: 0,
            optionName: '',
            optionNameShort: '',
        },
        betPage: 0,
        minBet: 0,
        maxBet: 0,
        priceStyle: 0,
        message: {
            timeoutId: 0,
            key: null,
            data: null,
        },
        uid: '',
        sportType: 0,
        leagueId: 0,
        leagueName: '',
        eventId: 0,
        oddsId: 0,
        point: 0,
        marketDisplayTypeName: '',
        marketType: 0,
        option: '',
        optionName: '',
        betCondition: '',
        isLive: false,
        price: 0,
        isMixParlay: false,
        marketPageType: MarketPage.Unknown,
        marketGroup: {
            id: 0,
            displayPriority: 0,
            homeTemplate: '',
            awayTemplate: '',
        },
    };
}
